import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { clientTzOffsetMinutes } from '../lib/circles/api';
import { PremiumCard, PremiumPage, PrimaryButton, SectionHeader } from '../components/premium';
import { appPath } from '../lib/appPaths';

type LeaderboardRow = {
  userId: string;
  name: string;
  score: number;
  streak: number | null;
  rank: number;
  highlight: string;
  isYou: boolean;
};

type IndividualPayload = {
  period: 'day' | 'week';
  dateKey: string;
  optedIn: boolean;
  you: LeaderboardRow | null;
  entries: LeaderboardRow[];
  totalOptedIn: number;
};

type CirclePayload = {
  period: 'day' | 'week';
  dateKey: string;
  circleId: string;
  circleName: string;
  optedIn: boolean;
  you: LeaderboardRow | null;
  entries: LeaderboardRow[];
  hiddenCount: number;
};

type MyCircle = { id: string; name: string };

function RankRow({ entry }: { entry: LeaderboardRow }) {
  return (
    <PremiumCard className={`!p-4 ${entry.isYou ? '!ring-1 !ring-amber-500/40' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/8 text-sm font-bold text-[var(--text-muted)]">
          {entry.rank}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-[var(--text-primary)]">
            {entry.name}
            {entry.isYou ? <span className="ml-1 text-xs text-amber-300">(You)</span> : null}
          </div>
          <div className="mt-0.5 text-xs text-[var(--text-muted)]">{entry.highlight}</div>
          {entry.streak != null && entry.streak > 0 ? (
            <div className="mt-1 text-[11px] font-medium text-amber-300">{entry.streak}-day streak</div>
          ) : null}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-bold tabular-nums text-white">{entry.score}</div>
          <div className="text-[11px] text-zinc-500">momentum</div>
        </div>
      </div>
    </PremiumCard>
  );
}

export function LeaderboardsPage() {
  const { user, setUserFields } = useAuth();
  const [tab, setTab] = useState<'individual' | 'circles'>('individual');
  const [period, setPeriod] = useState<'day' | 'week'>('day');
  const [individual, setIndividual] = useState<IndividualPayload | null>(null);
  const [circleData, setCircleData] = useState<CirclePayload | null>(null);
  const [myCircles, setMyCircles] = useState<MyCircle[]>([]);
  const [circleId, setCircleId] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [optBusy, setOptBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    setBusy(true);
    try {
      const tz = `tzOffset=${clientTzOffsetMinutes()}`;
      if (tab === 'individual') {
        const data = await api<IndividualPayload>(
          `/api/leaderboards/individual?period=${period}&${tz}`,
        );
        setIndividual(data);
      } else {
        const circles = await api<MyCircle[]>('/api/leaderboards/my-circles');
        setMyCircles(circles);
        const id = circleId || circles[0]?.id || '';
        if (id && id !== circleId) setCircleId(id);
        if (!id) {
          setCircleData(null);
          return;
        }
        const data = await api<CirclePayload>(
          `/api/leaderboards/circles/${id}?period=${period}&${tz}`,
        );
        setCircleData(data);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load leaderboards.');
    } finally {
      setBusy(false);
    }
  }, [tab, period, circleId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggleOptIn(next: boolean) {
    setOptBusy(true);
    setErr(null);
    try {
      const updated = await api<{ leaderboardsOptIn: boolean }>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify({ leaderboardsOptIn: next }),
      });
      setUserFields({ leaderboardsOptIn: Boolean(updated.leaderboardsOptIn) });
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not update preference.');
    } finally {
      setOptBusy(false);
    }
  }

  const optedIn = Boolean(user?.leaderboardsOptIn);
  const entries = tab === 'individual' ? individual?.entries ?? [] : circleData?.entries ?? [];
  const you = tab === 'individual' ? individual?.you : circleData?.you;

  return (
    <PremiumPage title="Leaderboards">
      <p className="mb-4 text-sm text-[var(--text-muted)]">
        Ranked by momentum score — how well you hit your own plan. Opt in to appear.
      </p>

      <PremiumCard className="!p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-white">Leaderboard opt-in</div>
            <p className="mt-0.5 text-xs text-zinc-500">
              {optedIn ? 'You appear on individual and circle boards.' : 'You are hidden until you opt in.'}
            </p>
          </div>
          <button
            type="button"
            disabled={optBusy}
            onClick={() => void toggleOptIn(!optedIn)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              optedIn
                ? 'bg-amber-500/20 text-amber-100 ring-1 ring-amber-500/30'
                : 'bg-white/8 text-zinc-300 ring-1 ring-white/10'
            }`}
          >
            {optBusy ? '…' : optedIn ? 'Opted in' : 'Opt in'}
          </button>
        </div>
      </PremiumCard>

      <div className="mt-4 flex gap-2">
        {(
          [
            ['individual', 'Individuals'],
            ['circles', 'Circles'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              tab === id
                ? 'bg-sky-500/20 text-sky-100 ring-1 ring-sky-500/30'
                : 'bg-white/5 text-zinc-400'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        {(
          [
            ['day', 'Today'],
            ['week', 'This week'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setPeriod(id)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              period === id
                ? 'bg-white/12 text-white ring-1 ring-white/20'
                : 'bg-white/5 text-zinc-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'circles' ? (
        myCircles.length ? (
          <div className="mt-3">
            <select
              value={circleId}
              onChange={(e) => setCircleId(e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none"
            >
              {myCircles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <PremiumCard className="mt-4 !p-4">
            <p className="text-sm text-zinc-400">Join a Circle to see circle leaderboards.</p>
            <Link to={appPath('/circles')} className="mt-3 inline-block text-sm font-semibold text-sky-300">
              Open Circles →
            </Link>
          </PremiumCard>
        )
      ) : null}

      {err ? <p className="mt-4 text-sm text-rose-300">{err}</p> : null}

      {you ? (
        <div className="mt-5">
          <SectionHeader title="Your rank" />
          <RankRow entry={you} />
        </div>
      ) : optedIn ? null : (
        <PremiumCard className="mt-5 !p-4">
          <p className="text-sm text-zinc-300">Opt in above to join the rankings.</p>
          <PrimaryButton className="mt-3" disabled={optBusy} onClick={() => void toggleOptIn(true)}>
            Opt into leaderboards
          </PrimaryButton>
        </PremiumCard>
      )}

      <div className="mt-5">
        <SectionHeader
          title={
            tab === 'individual'
              ? `Individuals${individual ? ` · ${individual.totalOptedIn} opted in` : ''}`
              : circleData
                ? `${circleData.circleName}${circleData.hiddenCount ? ` · ${circleData.hiddenCount} hidden` : ''}`
                : 'Circle'
          }
        />
        {busy && !entries.length ? (
          <p className="text-sm text-zinc-500">Loading…</p>
        ) : entries.length ? (
          <div className="space-y-2">
            {entries.map((e) => (
              <RankRow key={e.userId} entry={e} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-zinc-500">No ranked athletes yet for this board.</p>
        )}
      </div>
    </PremiumPage>
  );
}
